import React from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { CircularProgress, ThemeProvider, Tooltip, createTheme, useTheme } from '@mui/material'
import {
  Code as JsonIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  PersonOutline as OwnerIcon,
} from '@mui/icons-material'
import { PrivatePage } from '../component/PagePrivate'
import { ViewToggle } from '../component/ViewToggle'
import { NotificationV2 } from '../container/common/NotificationV2'
import { ProfileV2 } from '../container/common/ProfileV2'
import ResultView from '../container/result-v2/ResultView'
import { SearchResult } from '../store/searcher/reducer'
import { api, Response } from '../function/axios'
import { getNow } from '../function/date'
import { useAdminTheme } from '../hook/useAdminTheme'
import l from './SubscribersPageV2.module.css'

// Same refresh cadence as the user UI's in-progress polling.
const POLL_MS = 10000

export function ResultPageV2() {
  const { id } = useParams() as { id: string }
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const outerTheme = useTheme()
  const { theme, toggleTheme } = useAdminTheme()
  // result-v2 picks its light/dark tokens from the MUI palette mode, so the
  // admin theme switch is applied by a nested ThemeProvider.
  const muiTheme = React.useMemo(() => createTheme(outerTheme, { palette: { mode: theme } }), [outerTheme, theme])

  const [row, setRow] = React.useState<SearchResult | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [notFound, setNotFound] = React.useState(false)

  const load = React.useCallback(
    () =>
      // Same endpoint the admin's old ResultPage uses (fetchResultById).
      api({ method: 'GET', url: `/request-monitor/${id}` })
        .then(({ data }: Response) => {
          setRow(data)
          setNotFound(!data)
        })
        .catch(() => setNotFound(true))
        .finally(() => setLoading(false)),
    [id],
  )

  React.useEffect(() => {
    setLoading(true)
    load()
  }, [load])

  const status = (row as any)?.status
  React.useEffect(() => {
    if (status !== 'progress') return undefined
    const interval = setInterval(load, POLL_MS)
    return () => clearInterval(interval)
  }, [status, load])

  const downloadJson = () => {
    if (!row) return
    const blob = new Blob([JSON.stringify(row)], { type: 'application/json' })
    const href = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = href
    link.download = `${row.type}_${getNow()}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(href)
  }

  // The owner comes from ?subscriber= (set by subscriber_v2's Searches tab)
  // or, failing that, from the result's own userId.
  const ownerId = searchParams.get('subscriber') || (row as any)?.userId

  // Back always goes to the owner's subscriber page; the result is usually
  // opened in a new tab, so browser history has nothing useful to go back to.
  const back = () => {
    if (ownerId) navigate(`/users/subscriber_v2/${ownerId}?tab=searches`)
    else navigate('/users/subscribers_v2')
  }
  const isDeleted = !!(row as any)?.isDeleted

  const header = (
    <div className={`${l.theme} ${l.header}`} data-theme={theme}>
      <div className={l.crumb}>
        <small>Users / Subscribers / Search result</small>
        <b>Result #{id}</b>
      </div>
      <div className={l.spacer} />
      <ViewToggle dark={theme === 'dark'} />
      <Tooltip title={theme === 'dark' ? 'Light theme' : 'Dark theme'}>
        <button className={l.iconBtn} onClick={toggleTheme} aria-label="Toggle theme">
          {theme === 'dark' ? <LightModeIcon sx={{ fontSize: 17 }} /> : <DarkModeIcon sx={{ fontSize: 17 }} />}
        </button>
      </Tooltip>
      <NotificationV2 />
      <ProfileV2 />
    </div>
  )

  const adminActions = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {isDeleted && (
        <span className={l.pill} style={{ color: 'var(--danger)', background: 'color-mix(in srgb, var(--danger) 12%, transparent)', fontWeight: 700 }}>
          DELETED
        </span>
      )}
      {ownerId && (
        <Tooltip title="Open the subscriber who ran this search">
          <button className={l.chip} onClick={() => window.open(`/users/subscriber_v2/${ownerId}`, '_blank', 'noopener,noreferrer')}>
            <OwnerIcon sx={{ fontSize: 16 }} />
            Subscriber #{ownerId}
          </button>
        </Tooltip>
      )}
      <Tooltip title="Download JSON">
        <button className={l.chip} onClick={downloadJson}>
          <JsonIcon sx={{ fontSize: 16 }} />
          JSON
        </button>
      </Tooltip>
    </div>
  )

  return (
    <PrivatePage header={header}>
      <div className={`${l.theme} ${l.page}`} data-theme={theme}>
        {loading && !row ? (
          <div style={{ display: 'grid', placeItems: 'center', padding: 60 }}>
            <CircularProgress />
          </div>
        ) : notFound || !row ? (
          <div className={l.empty}>Search result not found</div>
        ) : (
          <ThemeProvider theme={muiTheme}>
            <ResultView data={row} inline adminActions={adminActions} onBack={back} backLabel={ownerId ? 'Back to subscriber' : 'Back to subscribers'} />
          </ThemeProvider>
        )}
      </div>
    </PrivatePage>
  )
}
